import React, { useEffect, useState, useRef } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { fmtAmount } from "@/lib/stayflowApi";
import { MapAnchoredPopover } from "./MapAnchoredPopover";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const Grab = ({ onMap }) => {
  const map = useMap();
  useEffect(() => { if (map) onMap(map); }, [map, onMap]);
  return null;
};

const FitBounds = ({ geo, userLoc }) => {
  const map = useMap();
  const lastKey = useRef("");
  useEffect(() => {
    if (!map || !window.google || geo.length === 0) return;
    // Only re-fit when the actual result set (or user location) changes — NOT on every
    // render/selection (geo is a fresh array each render, which caused re-zoom on tap).
    const key = geo.map((p) => p.id).join(",") + (userLoc?.lat != null ? "|u" : "");
    if (key === lastKey.current) return;
    lastKey.current = key;

    const pts = geo.map((p) => ({ lat: p.lat, lng: p.lng }));
    if (userLoc?.lat != null) pts.push({ lat: userLoc.lat, lng: userLoc.lng });
    const lats = pts.map((p) => p.lat), lngs = pts.map((p) => p.lng);
    const tiny = pts.length <= 1 || (Math.max(...lats) - Math.min(...lats) < 0.03 && Math.max(...lngs) - Math.min(...lngs) < 0.03);

    // Single/clustered results → comfortable neighbourhood zoom (Airbnb-style), never street-level.
    if (tiny) {
      map.setCenter({ lat: geo[0].lat, lng: geo[0].lng });
      map.setZoom(13);
      map.panBy(0, Math.round(window.innerHeight * 0.12));
      return;
    }
    const b = new window.google.maps.LatLngBounds();
    pts.forEach((p) => b.extend(p));
    map.fitBounds(b, { top: 150, left: 60, right: 60, bottom: Math.round(window.innerHeight * 0.4) });
    // Clamp so two nearby points don't zoom to the max.
    window.google.maps.event.addListenerOnce(map, "idle", () => { if (map.getZoom() > 14) map.setZoom(14); });
  }, [map, geo, userLoc]);
  return null;
};

// Full-background Google map with tappable price-pill markers.
export const PriceMarkerMap = ({ points = [], selectedId, onSelect, userLoc = null, focus = null, metaById = {}, onViewStay, onClose }) => {
  const geo = points.filter((p) => p.lat != null && p.lng != null);
  const [mapObj, setMapObj] = useState(null);
  const sel = selectedId ? geo.find((p) => p.id === selectedId) : null;
  const meta = sel ? metaById[sel.id] : null;

  if (!KEY) {
    return <div className="absolute inset-0 bg-paper-muted flex items-center justify-center text-ink-soft text-sm" data-testid="price-map-nokey">Map isn't configured yet.</div>;
  }

  const center = focus?.lat != null ? { lat: focus.lat, lng: focus.lng } : userLoc?.lat != null ? { lat: userLoc.lat, lng: userLoc.lng } : geo[0] ? { lat: geo[0].lat, lng: geo[0].lng } : { lat: 26.2, lng: 92.9 };

  return (
    <div className="absolute inset-0" data-testid="map-canvas">
      <APIProvider apiKey={KEY}>
        <Map
          mapId="DEMO_MAP_ID"
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={geo.length > 1 ? 11 : 13}
          gestureHandling="greedy"
          disableDefaultUI
          clickableIcons={false}
        >
          <Grab onMap={setMapObj} />
          <FitBounds geo={geo} userLoc={userLoc || focus} />
          {geo.map((p) => {
            const active = p.id === selectedId;
            return (
              <AdvancedMarker key={p.id} position={{ lat: p.lat, lng: p.lng }} zIndex={active ? 20 : 1} onClick={() => onSelect(p.id)}>
                <div data-testid={`price-marker-${p.id}`}
                  className={`rounded-full px-3 py-1.5 text-[13px] font-heading font-bold shadow-md border transition-transform ${
                    active ? "bg-ink text-white border-ink scale-110 shadow-lg" : "bg-white text-ink border-black/20"
                  }`}>
                  {fmtAmount(p.min_price_incl_gst ?? p.min_price_per_night) || "View"}
                </div>
              </AdvancedMarker>
            );
          })}
          {focus?.lat != null && (
            <AdvancedMarker position={{ lat: focus.lat, lng: focus.lng }} zIndex={6}>
              <div className="flex flex-col items-center" data-testid="map-focus-marker">
                <span className="rounded-full bg-ink text-white text-[11px] font-semibold px-2.5 py-1 shadow-md whitespace-nowrap">{focus.label}</span>
                <span className="w-3 h-3 -mt-0.5 rotate-45 bg-ink" />
              </div>
            </AdvancedMarker>
          )}
          {userLoc?.lat != null && (
            <AdvancedMarker position={{ lat: userLoc.lat, lng: userLoc.lng }} zIndex={5}>
              <div className="w-4 h-4 rounded-full bg-blue-600 border-2 border-white shadow-md" data-testid="user-location-dot" />
            </AdvancedMarker>
          )}
        </Map>
      </APIProvider>
      <MapAnchoredPopover
        map={mapObj}
        point={sel}
        meta={sel && meta ? { ...meta, id: sel.id } : null}
        onClose={() => onClose && onClose()}
        onView={() => sel && onViewStay && onViewStay(sel.id)}
      />
    </div>
  );
};

export default PriceMarkerMap;
